import React from 'react';
import LocalizedLink from '../atoms/LocalizedLink';
import Container from '../atoms/Container';
import Logo from '../atoms/Logo';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const navigation = {
    about: [
      { name: t('footer.links.aboutUs'), href: '/about' },
      { name: t('footer.links.accreditation'), href: '/accreditation' },
      { name: t('footer.links.facultyStaff'), href: '/faculty' },
      { name: t('footer.links.newsEvents'), href: '/news' },
      { name: t('footer.links.alumni'), href: '/alumni' },
      { name: t('footer.links.employment'), href: '/employment' },
    ],
    academics: [
      { name: t('footer.links.academicPrograms'), href: '/academics' },
      { name: t('footer.links.curriculum'), href: '/curriculum' },
      { name: t('footer.links.preschool'), href: '/preschool' },
      { name: t('footer.links.elementarySchool'), href: '/elementary-school' },
      { name: t('footer.links.middleSchool'), href: '/middle-school' },
      { name: t('footer.links.highSchool'), href: '/high-school' },
      { name: t('footer.links.learningSupport'), href: '/learning-support' },
      { name: t('footer.links.technology'), href: '/technology' },
      { name: t('footer.links.universityCounseling'), href: '/university-counseling' },
      { name: t('footer.links.academicResults'), href: '/academic-results' },
    ],
    studentLife: [
      { name: t('footer.links.schoolCalendar'), href: '/calendar' },
      { name: t('footer.links.activitiesClubs'), href: '/activities' },
      { name: t('footer.links.communityService'), href: '/community-service' },
      { name: t('footer.links.studentResources'), href: '/resources' },
      { name: t('footer.links.socialMedia'), href: '/social-media' },
    ],
    admissions: [
      { name: t('footer.links.admissionsProcess'), href: '/admissions' },
      { name: t('footer.links.parentResources'), href: '/parent-resources' },
    ],
    social: [
      {
        name: 'Facebook',
        href: 'https://www.facebook.com/p/TLC-International-School-Mauritania-100067427206332/',
        icon: (props: React.SVGProps<SVGSVGElement>) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              fillRule="evenodd"
              d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
      {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/company/mauritania-tlc-international-school',
        icon: (props: React.SVGProps<SVGSVGElement>) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              fillRule="evenodd"
              d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
    ],
  };

  return (
    <footer className="bg-white">
      <Container>
        <div className="py-14 md:py-18">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 md:gap-12">
            {/* School Information Column */}
            <div className="lg:col-span-1 space-y-6">
              <Logo size="lg" />
              <div className="text-gray-700 space-y-4 text-sm">
                <p className="font-semibold text-base text-tlc-navy">{t('footer.schoolInfo')}</p>
                <a 
                  href="https://maps.app.goo.gl/kwuWXMvdP3EvLwYH6" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-3 hover:text-tlc-navy transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-tlc-navy flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Nouakchott, Mauritania</span>
                </a>
                <a 
                  href="mailto:info@tlcmauritania.org" 
                  className="flex items-center space-x-3 hover:text-tlc-navy transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-tlc-navy flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>info@tlcmauritania.org</span>
                </a>
                <a 
                  href="tel:+22246041532" 
                  className="flex items-center space-x-3 hover:text-tlc-navy transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-tlc-navy flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>+222 46 04 15 32</span>
                </a>
              </div>
              
              <LocalizedLink 
                to="/admissions" 
                className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-tlc-navy hover:bg-tlc-navy/90 transition-colors"
              >
                {t('footer.applyNow')}
              </LocalizedLink>
              
              <div className="flex space-x-5 pt-2">
                {navigation.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-gray-500 hover:text-tlc-navy transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon className="h-6 w-6" aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation Columns */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-tlc-navy">
                {t('footer.categories.about')}
              </h3>
              <ul className="mt-5 space-y-3.5">
                {navigation.about.map((item) => (
                  <li key={item.name}>
                    <LocalizedLink
                      to={item.href}
                      className="text-sm text-gray-500 hover:text-tlc-navy"
                    >
                      {item.name}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-tlc-navy">
                {t('footer.categories.academics')}
              </h3>
              <ul className="mt-5 space-y-3.5">
                {navigation.academics.map((item) => (
                  <li key={item.name}>
                    <LocalizedLink
                      to={item.href}
                      className="text-sm text-gray-500 hover:text-tlc-navy"
                    >
                      {item.name}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-tlc-navy">
                {t('footer.categories.studentLife')}
              </h3>
              <ul className="mt-5 space-y-3.5">
                {navigation.studentLife.map((item) => (
                  <li key={item.name}>
                    <LocalizedLink
                      to={item.href}
                      className="text-sm text-gray-500 hover:text-tlc-navy"
                    >
                      {item.name}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-tlc-navy">
                {t('footer.categories.admissions')}
              </h3>
              <ul className="mt-5 space-y-3.5">
                {navigation.admissions.map((item) => (
                  <li key={item.name}>
                    <LocalizedLink
                      to={item.href}
                      className="text-sm text-gray-500 hover:text-tlc-navy"
                    >
                      {item.name}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-gray-200">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-3">
                  <img src="/images/logos/msa_logo.png" alt="MSA-CESS Accreditation" className="h-12 w-auto" />
                  <div className="text-xs text-gray-500">
                    <p>{t('footer.accreditation.through')}</p>
                    <p>{t('footer.accreditation.organization')}</p>
                  </div>
                </div>
                <img src="/images/logos/1808480.jpg" alt="NEASC Accreditation" className="h-12 w-auto" />
                <img src="/images/logos/Collegeboard-Logo.png" alt="College Board" className="h-12 w-auto" />
                <img src="/images/logos/ap_logo.png" alt="Advanced Placement" className="h-12 w-auto" />
              </div>
              <p className="mt-8 md:mt-0 text-sm text-gray-500">
                &copy; {currentYear} TLC International School. {t('footer.rights')}.
              </p>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer; 